// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import CaretDown from "@store/Library/Components/Icons/CaretDown";
import CaretUp from "@store/Library/Components/Icons/CaretUp";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { css } from "styled-components";

interface ContentZoneProps extends ExpandStyles<ContentZoneStyles> {
    title?: string | null;
    content: string;
    contentCellWidth: GridWidths | GridWidths[];
    readMore?: string;
    readMoreLabel?: string | null;
    readLessLabel?: string | null;
    titleVariant?: "h2" | "h3" | "h4" | "h5";
    titleForwardAs?: "h2" | "h3" | "h4" | "h5";
}
export interface ContentZoneStyles {
    container?: GridContainerProps;
    contentCell?: GridItemProps;
    readMoreLink?: LinkPresentationProps;
    titleStyle?: TypographyPresentationProps;
    shortDescriptionStyle?: TypographyPresentationProps;
}

export const contentZoneStyles: ContentZoneStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
            margin-bottom: 2em;
        `,
    },
    contentCell: {
        css: css`
            flex-direction: column;
        `,
    },
    readMoreLink: {
        css: css`
            margin: 1em 0;
        `,
    },
    titleStyle: {
        variant: "h4",
        forwardAs: "h2",
        css: spacingStyles(),
    },
    shortDescriptionStyle: {
        css: css`
            > div {
                ul,
                ol {
                    margin-left: ${pxToRem(3)};
                }
            }
        `,
    },
};

const getReadMoreLines = (readMore: string) => {
    if (readMore.startsWith("[") && readMore.endsWith("]")) {
        const numberArray: string[] = readMore.substring(1, readMore.length - 1).split(",");
        const populatedByNumbers = numberArray.every(function (el) {
            return typeof parseInt(el, 10) === "number";
        });
        if (numberArray.length === 5 && populatedByNumbers) {
            return numberArray;
        }
        return false;
    }
    if (parseInt(readMore, 10)) {
        // Output as array even if only one item so that getReadMoreLines always outputs as array or false.
        return [readMore];
    }
    return false;
};

const ContentZone: React.FC<ContentZoneProps> = ({
    title,
    content,
    contentCellWidth,
    readMore,
    readMoreLabel = translate("See More"),
    readLessLabel = translate("See Less"),
    titleVariant,
    titleForwardAs,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(contentZoneStyles, extendedStyles, overridedStyles);

    const [showMoreFeature, setShowMoreFeature] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const readMoreLines = readMore ? getReadMoreLines(readMore) : false;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const contentCellStyle = {
        ...styles.contentCell,
        width: contentCellWidth,
    };

    const titleStyle = {
        ...styles.titleStyle,
        variant: titleVariant || styles.titleStyle?.variant,
        forwardAs: titleForwardAs || styles.titleStyle?.forwardAs,
    };

    const readMoreLinkStyle = mergeProps<LinkPresentationProps>(
        linkGetActionLinkProps(showMore ? CaretUp : CaretDown, "right"),
        {
            ...styles.readMoreLink,
        },
    );

    const shortDescriptionWrapperStyle =
        readMoreLines && !showMore
            ? {
                  css: css`
                      /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
                      display: -webkit-box;
                      overflow: hidden;
                      ${readMoreLines.length === 5
                          ? breakpointValues(readMoreLines, "-webkit-line-clamp")
                          : `-webkit-line-clamp:${readMoreLines[0]};`}
                      -webkit-box-orient: vertical;
                      /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */

                      > div {
                          height: fit-content;
                      }
                      ${({ theme }) => {
                          const fontSize = theme.design.typography.size.base;
                          const lineHeight = theme.design.typography.lineHeight.base;

                          return css`
                              font-size: ${fontSize};
                              line-height: ${lineHeight};
                              ${readMoreLines.length === 5
                                  ? breakpointValues(
                                        readMoreLines.map(lines => `calc((${lineHeight} * ${fontSize}) * ${lines})`),
                                        "max-height",
                                    )
                                  : `max-height:${`calc((${lineHeight} * ${fontSize}) * ${readMoreLines[0]})`};`}
                          `;
                      }}
                      ${styles.shortDescriptionStyle?.css}
                  `,
              }
            : styles.shortDescriptionStyle;

    const timeout = (delay: number) => {
        return new Promise(res => setTimeout(res, delay));
    };

    const setContentZoneProps = async () => {
        const contentZoneWrapper = wrapperRef.current;
        const contentZoneContent = contentRef.current;
        const contentZoneWrapperHeight = contentZoneWrapper?.offsetHeight;
        const contentZoneContentHeight = contentZoneContent?.scrollHeight;

        if (readMoreLines) {
            setShowMoreFeature(false);
            await timeout(100);
            if (
                contentZoneWrapperHeight &&
                contentZoneContentHeight &&
                contentZoneWrapperHeight >= contentZoneContentHeight
            ) {
                setShowMoreFeature(false);
            } else {
                setShowMoreFeature(true);
            }
        }
    };

    useEffect(() => {
        setContentZoneProps();
        const onResize = debounce(() => setContentZoneProps(), 150);
        window.addEventListener("resize", onResize, { passive: true });
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [wrapperRef]);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...contentCellStyle}>
                {title && <Typography {...titleStyle}>{title}</Typography>}
                <StyledWrapper ref={wrapperRef} {...shortDescriptionWrapperStyle}>
                    <StyledWrapper as="div" ref={contentRef}>
                        {parse(content, parserOptions)}
                    </StyledWrapper>
                </StyledWrapper>
                {readMoreLines && showMoreFeature && (
                    <Link {...readMoreLinkStyle} onClick={() => setShowMore(!showMore)}>
                        {!showMore ? readMoreLabel : readLessLabel}
                    </Link>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default ContentZone;
